import { Children, type PropsWithChildren, type Ref, cloneElement, isValidElement } from 'react';

import ChevronLeft from '@/assets/svg/chevron-left.svg?react';
import { cn } from '@/lib/utils';

type Props = {
  ref?: Ref<HTMLDivElement>;
  isOpen: boolean;
  onOpenChange?: (isOpen: boolean) => void;
};

type PanelDrawerChildProps = {
  isOpen: boolean;
  [key: string]: unknown;
};

const PanelDrawer = ({ ref, children, isOpen, onOpenChange }: PropsWithChildren<Props>) => {
  const handleDrawerToggle = () => {
    const newIsOpen = !isOpen;
    onOpenChange?.(newIsOpen);
  };

  const wrappedChildren = Children.map(children, (child) => {
    if (isValidElement<PanelDrawerChildProps>(child)) {
      return cloneElement(child, { ...(child.props ?? {}), isOpen });
    }
  });

  return (
    <div ref={ref} data-testid="panel-drawer" className="absolute -inset-x-0.5 top-full">
      <div
        className={cn(
          'overflow-hidden rounded-b-2xl border-2 border-t-0 border-gray-300 bg-gray-100 transition-all duration-300 ease-[cubic-bezier(0.645,0.045,0.355,1)]',
          isOpen
            ? 'visible translate-y-0 shadow-[0_8px_8px_0_#0000000d]'
            : 'invisible -translate-y-full',
        )}
      >
        {wrappedChildren}
      </div>
      <div
        role="button"
        data-testid="panel-drawer-toggle"
        className="absolute top-0 left-1/2 flex h-4 w-20 -translate-x-1/2 cursor-pointer items-center justify-center rounded-b-[5px] bg-gray-200 hover:bg-gray-300 active:bg-gray-400"
        onClick={handleDrawerToggle}
      >
        <ChevronLeft
          className={cn('transition-transform duration-300', isOpen ? 'rotate-90' : '-rotate-90')}
        />
      </div>
    </div>
  );
};

export default PanelDrawer;
